import type { PricerDatecQuoteFull } from '../types/pricer-datec.types';
import { pctDatec } from '../lib/formato-datec';

/**
 * F6 T14: la ficha que el servidor le entrega a un OBSERVADOR (acceso OBSERVACION) viene recortada a
 * precios: sin costos, sin márgenes, sin identificadores de ítem. Se reconoce por `nivelDeVista`.
 *
 * ⚠ Esa ficha NO se puede cargar en el editor: el motor del Pricer recalcularía con costo 0 y la pantalla
 * (y el PDF, que se arma en el navegador) mostrarían precios inventados. Se muestra tal cual vino.
 */
export interface FichaDeObservador {
  id: string;
  code: string;
  name: string | null;
  status: string;
  clientName: string;
  fecha: string;
  validezDias: number;
  tipoQuote: string;
  revision: number;
  submittedAt: string | null;
  resolvedAt: string | null;
  validaHasta: string | null;
  totalFacturado: number;
  totalConBoleta: number;
  itemsCount: number;
  createdBy: { firstName: string; lastName: string };
  payload: {
    params: { moneda?: string; tcPresentacion?: number | null };
    items: Array<{
      num: number | string;
      numParte: string;
      descripcion: string;
      marcaProducto: string;
      cantidad: number | string;
      precioUnitario: number | string;
      totalLinea: number | string;
      boleta: number;
    }>;
    totales: { totalFacturado: number; boleta: number; totalConBoleta: number };
  };
  aprobacion: { estado: string | null; resueltaEl: string | null; avisosDeEnvio?: unknown };
  cancelacion: {
    motivo: string | null;
    por: { firstName: string; lastName: string } | null;
    fecha: string;
  } | null;
  historial: Array<{
    accion: string;
    desde: string | null;
    hacia: string | null;
    cuando: string;
    quien: string | null;
  }>;
  nivelDeVista: 'SOLO_PRECIOS';
  /**
   * Por qué viene recortada (FP-4): `OBSERVADOR` (acceso de observación) o `SIN_COSTOS` (ve la cotización pero su
   * rol no tiene «Ver costos y márgenes»). Sin el campo, se asume observador.
   */
  vistaPor?: 'OBSERVADOR' | 'SIN_COSTOS';
}

export function esFichaDeObservador(q: unknown): q is FichaDeObservador {
  return (
    typeof q === 'object' &&
    q !== null &&
    (q as { nivelDeVista?: unknown }).nivelDeVista === 'SOLO_PRECIOS'
  );
}

/** Para donde el tipo estático es el de la ficha completa. */
export function comoObservador(q: PricerDatecQuoteFull | null): FichaDeObservador | null {
  return esFichaDeObservador(q) ? q : null;
}

/**
 * El margen de una fila de lista. Las filas que el servidor recortó para un observador no traen
 * `margenGlobal`: se muestra «—» y no «NaN%».
 */
export function margenParaLista(margenGlobal: number | null | undefined): string {
  if (typeof margenGlobal !== 'number' || !Number.isFinite(margenGlobal)) return '—';
  return pctDatec(margenGlobal * 100);
}
